'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import PageLayout from '@/components/layout/PageLayout';
import AuthGuard from '@/components/ui/AuthGuard';
import {
  Activity,
  ArrowLeftRight,
  ArrowUpRight,
  BarChart3,
  Briefcase,
  Crosshair,
  FileSearch,
  Gauge,
  History,
  Landmark,
  Layers,
  Radar,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  UserSearch,
  Zap,
} from 'lucide-react';

interface Tool {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  featured?: boolean;
}

interface ToolGroup {
  id: string;
  label: string;
  blurb: string;
  icon: React.ReactNode;
  tools: Tool[];
}

const TOOL_GROUPS: ToolGroup[] = [
  {
    id: 'daily-desk',
    label: 'Daily desk',
    blurb: 'The tools you open every day — broker, orders and data operations',
    icon: <Briefcase className="h-4 w-4" />,
    tools: [
      {
        title: 'Broker Platform',
        description: 'Broker credentials and scheduled order management',
        href: '/broker-platform',
        icon: <Landmark className="h-5 w-5" />,
      },
      {
        title: 'Data Update',
        description: 'Refresh stock market data from external sources',
        href: '/data-update',
        icon: <RefreshCw className="h-5 w-5" />,
      },
      {
        title: 'Stock Kundali',
        description: 'The full birth chart of every stock — fundamentals to destiny',
        href: '/stocks',
        icon: <FileSearch className="h-5 w-5" />,
      },
    ],
  },
  {
    id: 'discover',
    label: 'Discover',
    blurb: 'Screeners and scanners that surface unusual market activity',
    icon: <Radar className="h-4 w-4" />,
    tools: [
      {
        title: 'Smart Stock Screener',
        description: 'Buying pressure, circuits, volume breakouts, price surges and 52-week levels',
        href: '/filtered-tick-analysis',
        icon: <SlidersHorizontal className="h-5 w-5" />,
        featured: true,
      },
      {
        title: 'F&O RSI Radar',
        description: 'Monthly, weekly, daily and hourly RSI across the F&O universe with 50-cross, alignment and divergence filters',
        href: '/rsi-radar',
        icon: <Activity className="h-5 w-5" />,
        featured: true,
      },
      {
        title: 'Market Depth Scanner',
        description: 'Real-time bid-ask and order book depth by market-cap range',
        href: '/tick-analysis',
        icon: <Layers className="h-5 w-5" />,
      },
      {
        title: 'Volume Analyzer',
        description: 'Stocks trading at unusual volume vs their historical averages',
        href: '/volume-analyzer',
        icon: <BarChart3 className="h-5 w-5" />,
      },
      {
        title: 'Volume Spike Scanner',
        description: 'Live volume-spike detection, streamed as spikes are discovered',
        href: '/volume-scanner',
        icon: <Zap className="h-5 w-5" />,
      },
    ],
  },
  {
    id: 'monitor',
    label: 'Monitor',
    blurb: 'Exchange surveillance lists — entries, stages and exits',
    icon: <ShieldCheck className="h-4 w-4" />,
    tools: [
      {
        title: 'ESM Stock Analysis',
        description: 'Stocks under the Enhanced Surveillance Measure framework',
        href: '/esm-stock-analysis',
        icon: <ShieldCheck className="h-5 w-5" />,
      },
      {
        title: 'ASM Stock History',
        description: 'ASM-listed stocks with interactive charts and filtering',
        href: '/asm-stock-history',
        icon: <History className="h-5 w-5" />,
      },
      {
        title: 'GSM Stock Analysis',
        description: 'Graded Surveillance Measure stocks with stage tracking',
        href: '/gsm-stock-analysis',
        icon: <Gauge className="h-5 w-5" />,
      },
    ],
  },
  {
    id: 'money-flow',
    label: 'Follow the money',
    blurb: 'Where informed capital is moving',
    icon: <ArrowLeftRight className="h-4 w-4" />,
    tools: [
      {
        title: 'Bulk & Block Deals',
        description: 'Large negotiated trades across NSE and BSE',
        href: '/deals',
        icon: <ArrowLeftRight className="h-5 w-5" />,
      },
      {
        title: 'Insider & SAST Deals',
        description: 'Insider trading disclosures and SAST filings',
        href: '/deals/insider-sast',
        icon: <UserSearch className="h-5 w-5" />,
      },
      {
        title: 'Big Buy at Low',
        description: 'Significant buying activity near lower price levels',
        href: '/big-buy-at-low',
        icon: <Crosshair className="h-5 w-5" />,
      },
    ],
  },
];

function ToolCard({ tool }: { tool: Tool }) {
  return (
    <Link
      href={tool.href}
      className={`group relative flex items-start gap-4 border-b border-r border-zinc-200 dark:border-zinc-800 p-5 transition-colors hover:bg-white dark:hover:bg-zinc-900 ${
        tool.featured ? 'bg-emerald-50/60 dark:bg-emerald-900/20' : ''
      }`}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 dark:border-zinc-700 text-emerald-700 dark:text-emerald-400 transition-colors group-hover:border-emerald-600 dark:group-hover:border-emerald-400">
        {tool.icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-base font-semibold text-zinc-900 dark:text-white">
            {tool.title}
          </span>
          {tool.featured && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              <Star className="h-2.5 w-2.5 fill-current" />
              Featured
            </span>
          )}
        </span>
        <span className="mt-1 block text-sm leading-relaxed text-zinc-500 dark:text-zinc-400 line-clamp-2">
          {tool.description}
        </span>
      </span>

      <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-300 dark:text-zinc-600 transition-all group-hover:text-emerald-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Link>
  );
}

export default function ToolsPage() {
  const [query, setQuery] = useState('');

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TOOL_GROUPS;
    return TOOL_GROUPS.map((group) => ({
      ...group,
      tools: group.tools.filter(
        (tool) =>
          tool.title.toLowerCase().includes(q) ||
          tool.description.toLowerCase().includes(q),
      ),
    })).filter((group) => group.tools.length > 0);
  }, [query]);

  const totalTools = TOOL_GROUPS.reduce((n, g) => n + g.tools.length, 0);

  return (
    <PageLayout>
      <AuthGuard>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          {/* Header */}
          <header className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">The desk</p>
              <h1 className="mt-3 text-5xl md:text-6xl text-zinc-900 dark:text-white">
                Tools
              </h1>
              <p className="mt-2 max-w-xl text-base text-zinc-500 dark:text-zinc-400">
                {totalTools} specialised tools for market analysis and stock discovery,
                grouped by what you&apos;re trying to do.
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools…"
                className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 py-2.5 pl-11 pr-4 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-colors"
              />
            </div>
          </header>

          {/* Groups */}
          <div className="mt-10 space-y-12">
            {filteredGroups.map((group) => (
              <section key={group.id}>
                <div className="mb-5 flex items-center gap-3 border-t border-zinc-900 dark:border-zinc-100 pt-4">
                  <span className="flex h-8 w-8 items-center justify-center text-emerald-700 dark:text-emerald-400">
                    {group.icon}
                  </span>
                  <div>
                    <h2 className="font-display text-2xl text-zinc-900 dark:text-white">
                      {group.label}
                      <span className="ml-2 font-sans font-mono text-xs text-zinc-400 dark:text-zinc-500">
                        {group.tools.length}
                      </span>
                    </h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">{group.blurb}</p>
                  </div>
                </div>

                <div className="grid border-l border-t border-zinc-200 dark:border-zinc-800 md:grid-cols-2 xl:grid-cols-3">
                  {group.tools.map((tool) => (
                    <ToolCard key={tool.href} tool={tool} />
                  ))}
                </div>
              </section>
            ))}

            {/* Empty state */}
            {filteredGroups.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 dark:border-white/[0.1] py-20 text-center">
                <Search className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                <p className="mt-4 text-base font-medium text-zinc-700 dark:text-zinc-300">
                  No tools match &ldquo;{query}&rdquo;
                </p>
                <button
                  onClick={() => setQuery('')}
                  className="mt-3 text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </PageLayout>
  );
}
